/**
 * "Recent" (plan docs/plans/lms-plan.md 7.1; R9, R24; WP-41): the last five
 * completions, newest first, each with its Download certificate action
 * (components/CertificateRow: the presigned URL, 10.4; "Certificate pending"
 * with Retry, 10.3). The whole transcript is on the Records tab.
 */
import { Link } from 'react-router-dom';
import { Award } from 'lucide-react';
import EmptyState from '../../../ui/EmptyState';
import { CertificateRow } from '../../components/CertificateRow';
import { formatDay } from '../../components/DueBadge';
import { Section, SectionError, SectionSkeleton } from './Section';
import { coursePath } from './homeData';
import type { TranscriptItem } from '../../api/completions';
import type { Loaded } from './useLoad';

/** 5.5 lms_completions.kind in words. */
export const COMPLETION_KIND_WORDS: Readonly<Record<string, string>> = {
  quiz: 'Quiz',
  read_ack: 'Acknowledgement',
  competency: 'Competency',
  external: 'External certificate',
};

export function RecentList({ recent }: { recent: Loaded<TranscriptItem[]> }) {
  const rows = recent.data ?? [];
  return (
    <Section
      title="Recent"
      icon={Award}
      headingId="lms-home-recent-title"
      data-testid="lms-home-recent"
      actions={
        <Link
          to="/lms/records"
          className="text-sm font-medium text-primary-600 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
        >
          All records
        </Link>
      }
    >
      {recent.loading ? (
        <SectionSkeleton rows={2} />
      ) : recent.error ? (
        <SectionError message={recent.error} onRetry={recent.reload} data-testid="lms-home-recent-error" />
      ) : rows.length === 0 ? (
        <EmptyState icon={Award} title="No completions yet" hint="Trainings you complete, and their certificates, appear here." />
      ) : (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700" data-testid="lms-recent-list">
          {rows.map((c) => {
            const title = c.course?.title ?? 'Course';
            return (
              <li
                key={c.id}
                className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                data-testid="lms-recent-row"
              >
                <div className="min-w-0 flex-1">
                  <Link
                    to={coursePath(c.course_id)}
                    className="text-sm font-medium text-gray-900 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100 dark:hover:text-primary-400"
                  >
                    {title}
                  </Link>
                  <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                    {[
                      COMPLETION_KIND_WORDS[c.kind] ?? c.kind,
                      `Completed ${formatDay(c.completed_at)}`,
                      typeof c.score_pct === 'number' ? `${c.score_pct}%` : null,
                    ]
                      .filter(Boolean)
                      .join(' \u00b7 ')}
                  </p>
                </div>
                <CertificateRow
                  completionId={c.id}
                  kind={c.kind}
                  category={c.course?.category ?? null}
                  certificate={c.certificate}
                  courseTitle={title}
                />
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

export default RecentList;
